import { Box, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiActivity, FiBox, FiShield, FiGlobe, FiLayers } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { MainContainer } from "../../components/container/MainContainer";
import { Supply_chain } from "../../db_access/supply_chain";
import { Structure } from "../../db_access/structure";
import routing from "../../routing";
import "../../style/pages-style/Organization/organizationDashboard.css";
import MetricCard from "../../app_components/MetricCard";

const OrganizationDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Basic mock stats until we have aggregated endpoints, or we can fetch what we can
  const [supplyChainCount, setSupplyChainCount] = useState(0);
  const [structureCount, setStructureCount] = useState(0);

  useEffect(() => {
    // Fetch basic counts to make the dashboard feel alive
    Supply_chain.get_from_my_workspace()
      .then(res => res.json())
      .then(data => setSupplyChainCount(Array.isArray(data) ? data.length : 0))
      .catch(() => { });

    Structure.get_mine()
      .then(res => res.json())
      .then(data => setStructureCount(Array.isArray(data) ? data.length : 0))
      .catch(() => { });
  }, []);

  const theme = useTheme();
  const MODULES = [
    {
      id: "supply-chain",
      title: "Supply Chain",
      desc: "Map value chains and track supplier footprints.",
      icon: FiGlobe,
      statValue: supplyChainCount,
      statLabel: "Active Chains",
      route: routing.organization.supply_chain.dashboard,
      colorClass: "org-module--supply-chain"
    },
    {
      id: "structure",
      title: "Organization",
      desc: "Define corporate assets and operational boundaries.",
      icon: FiLayers,
      statValue: structureCount,
      statLabel: "Facilities",
      route: routing.organization.structure.dashboard,
      colorClass: "org-module--structure"
    },
    {
      id: "sustainability",
      title: "Sustainability",
      desc: "Track Net Zero paths and decarbonization targets.",
      icon: FiActivity,
      statValue: "1",
      statLabel: "Active Path",
      route: routing.organization.sustainability.net_zero,
      colorClass: "org-module--sustainability"
    },
    {
      id: "compliance",
      title: "Compliance",
      desc: "Monitor alignment with global regulatory frameworks.",
      icon: FiShield,
      statValue: "85%",
      statLabel: "Readiness",
      route: routing.organization.compliance.sbti,
      colorClass: "org-module--compliance"
    }
  ];

  return (
    <MainContainer>
      {/* ── Hero Section ───────────────────────────────────────── */}
      <Box className="org-hero">
        <Box className="org-hero__content">
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Box sx={{
              p: 1.5,
              borderRadius: "16px",
              background: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <FiBox size={32} color="#ffffff" />
            </Box>
            <Typography sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 700, letterSpacing: 1, fontSize: "12px", textTransform: "uppercase" }}>
              Enterprise Intelligence
            </Typography>
          </Box>
          <Typography className="org-hero__title">
            Organizational Intelligence Hub
          </Typography>
          <Typography className="org-hero__subtitle">
            A centralized command center for your company's structure, external supply chain emissions, sustainability goals, and global compliance tracking.
          </Typography>
        </Box>
      </Box>

      {/* ── High-Level Aggregated KPIs ─────────────────────────── */}
      <Box className="org-stats-grid" sx={{ mb: 4 }}>
        <MetricCard
          title={t("Operational Entities")}
          value={structureCount}
          icon={FiLayers}
          accent="#8b5cf6"
          bg={theme.palette.mode === 'dark' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(168, 85, 247, 0.1)'}
          delay={0.1}
        />

        <MetricCard
          title={t("Connected Supply Chains")}
          value={supplyChainCount}
          icon={FiGlobe}
          accent="#0284c7"
          bg={theme.palette.mode === 'dark' ? 'rgba(2, 132, 199, 0.15)' : 'rgba(14, 165, 233, 0.1)'}
          delay={0.2}
        />

        <MetricCard
          title={t("Net Zero Path")}
          value={t("Tracking")}
          icon={FiActivity}
          accent="#16a34a"
          bg={theme.palette.mode === 'dark' ? 'rgba(22, 163, 74, 0.15)' : 'rgba(34, 197, 94, 0.1)'}
          delay={0.3}
        />

        <MetricCard
          title={t("Compliance Score")}
          value="85%"
          icon={FiShield}
          accent="#ea580c"
          bg={theme.palette.mode === 'dark' ? 'rgba(234, 88, 12, 0.15)' : 'rgba(249, 115, 22, 0.1)'}
          delay={0.4}
        />
      </Box>

      {/* ── Module Traversal Tiles ─────────────────────────────── */}
      <Box className="org-modules-section">
        <Typography className="org-modules-header">Ecosystem Pillars</Typography>
        <Box className="org-modules-grid">
          {MODULES.map((mod, i) => {
            const Icon = mod.icon;
            return (
              <Box
                key={mod.id}
                className={`org-module-tile ${mod.colorClass}`}
                style={{ animationDelay: `${0.2 + i * 0.15}s` }}
                onClick={() => navigate(mod.route)}
              >
                <Box className="org-module-tile__header">
                  <Box className="org-module-tile__icon-box">
                    <Icon size={24} />
                  </Box>
                  <FiArrowRight size={20} className="org-module-tile__arrow" />
                </Box>
                <Typography className="org-module-tile__title">{mod.title}</Typography>
                <Typography className="org-module-tile__desc">{mod.desc}</Typography>
                <Box className="org-module-tile__footer">
                  <Box>
                    <Typography className="org-module-tile__stat-value">{mod.statValue}</Typography>
                    <Typography className="org-module-tile__stat-label">{mod.statLabel}</Typography>
                  </Box>
                  <Box className="org-module-tile__arrow-circle">
                    <FiArrowRight size={14} className="org-module-tile__arrow-icon" />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </MainContainer>
  );
};

export default OrganizationDashboard;
